import React from "react";
import { observer } from "mobx-react";
import { Box, Grid2, Paper, useTheme } from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { RedemptionDashboardStore } from "../../../../stores/dashboards/RedemptionDashboardStore";
import { formatRedemptionCount } from "./redemptionFormat";
import { RedemptionEmptyState, RedemptionSectionLabel } from "./RedemptionSection";
import RedemptionHint from "./RedemptionHint";

interface RedemptionConsumptionProps {
  store: RedemptionDashboardStore;
}

const RedemptionConsumption: React.FC<RedemptionConsumptionProps> = ({
  store,
}) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const data = store.summaryResult?.data;

  if (!data || store.summaryError) {
    return null;
  }

  const buckets = data.over_time?.buckets ?? [];
  const cities = data.top_cities?.rows ?? [];
  const maxUnits = buckets.reduce(
    (peak, bucket) => Math.max(peak, bucket.units),
    0,
  );
  const maxCityUnits = cities.reduce(
    (peak, city) => Math.max(peak, city.units),
    0,
  );

  return (
    <Box sx={{ mb: 4 }}>
      <RedemptionSectionLabel
        label="Consumption pattern"
        how={data.over_time?.how}
      />
      <Grid2 container spacing={1.75}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              height: "100%",
              borderRadius: 2,
              borderColor: colors.lightGray,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <CustomTypography variant="heading5" color={colors.navyText}>
                Redeemed over time
              </CustomTypography>
              <RedemptionHint how={data.over_time?.how} fontSize={14} />
            </Box>
            <CustomTypography
              variant="body2"
              color={colors.greyTextColor}
              sx={{ mt: 0.25, mb: 2 }}
            >
              Units per week
            </CustomTypography>
            {buckets.length === 0 ? (
              <RedemptionEmptyState message="No weekly buckets for this filter." />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 1.5,
                  height: 148,
                  pt: 1,
                }}
              >
                {buckets.map((bucket) => {
                  const height =
                    maxUnits <= 0
                      ? 0
                      : Math.max(
                          bucket.units > 0 ? 6 : 0,
                          (bucket.units / maxUnits) * 96,
                        );
                  return (
                    <Box
                      key={`${bucket.week}-${bucket.start}`}
                      title={`${bucket.week} · ${bucket.start}`}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 0.75,
                      }}
                    >
                      <CustomTypography
                        variant="heading8"
                        fontWeight={700}
                        color={colors.navyText}
                        sx={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        {formatRedemptionCount(bucket.units)}
                      </CustomTypography>
                      <Box
                        sx={{
                          width: "60%",
                          height,
                          borderRadius: "6px 6px 0 0",
                          background: `linear-gradient(180deg, ${colors.purple}, ${colors.purpleDark})`,
                        }}
                      />
                      <CustomTypography
                        variant="heading9"
                        fontWeight={600}
                        color={colors.greyTextColor}
                      >
                        {bucket.week}
                      </CustomTypography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              height: "100%",
              borderRadius: 2,
              borderColor: colors.lightGray,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <CustomTypography variant="heading5" color={colors.navyText}>
                Top cities by units
              </CustomTypography>
              <RedemptionHint how={data.top_cities?.how} fontSize={14} />
            </Box>
            <CustomTypography
              variant="body2"
              color={colors.greyTextColor}
              sx={{ mt: 0.25, mb: 2 }}
            >
              Where products are consumed
            </CustomTypography>
            {cities.length === 0 ? (
              <RedemptionEmptyState message="No cities for this filter." />
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                {cities.map((city) => {
                  const width =
                    maxCityUnits <= 0
                      ? 0
                      : Math.max(0, (city.units / maxCityUnits) * 100);
                  return (
                    <Box
                      key={city.city}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 120px) 1fr auto",
                        alignItems: "center",
                        gap: 1.25,
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        color={colors.text}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={city.city}
                      >
                        {city.city}
                      </CustomTypography>
                      <Box
                        sx={{
                          height: 14,
                          borderRadius: "5px",
                          overflow: "hidden",
                          bgcolor: colors.greyBackground,
                        }}
                      >
                        <Box
                          sx={{
                            width: `${width}%`,
                            height: "100%",
                            borderRadius: "5px",
                            background: `linear-gradient(90deg, ${colors.purple}, ${colors.purpleDark})`,
                          }}
                        />
                      </Box>
                      <CustomTypography
                        variant="body2"
                        fontWeight={600}
                        color={colors.navyText}
                        sx={{
                          fontVariantNumeric: "tabular-nums",
                          minWidth: 36,
                          textAlign: "right",
                        }}
                      >
                        {formatRedemptionCount(city.units)}
                      </CustomTypography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default observer(RedemptionConsumption);
